import React from 'react'
import { Checkbox, Select } from '@surecompanies/core_components'

import { NOTE_TYPE_OPTIONS, RECON_STATUS_OPTIONS } from './helpers'
import PaymentTypeFilter from './PaymentTypeFilter'
import type { ClientOption, MonthOption } from './hooks/useTransactionDetails'
import styles from './TransactionFilters.module.scss'

/**
 * The filter bar: search, Payment Type (a "Select all + Apply" dropdown —
 * see PaymentTypeFilter.tsx — matching the design mock's draft/Apply
 * interaction), Month + Client selects (Client is the same company filter as
 * the Benefits tab — see useTransactionDetails), Recon
 * status multiselect, Note type multiselect + "Note contains" box (separate
 * from search, so it is always clear why a row matched), the Split-only and
 * Has-absorption checkboxes, Reset and Export. Every OTHER
 * filter here applies live as it changes (the app's own Select conventions,
 * e.g. DrawerSectionPaymentStatus) — Payment Type is the one exception.
 */

interface TransactionFiltersProps {
  search: string
  onSearchChange: (value: string) => void
  onApplySearch: (value: string) => void

  selectedSources: string[]
  onSourcesChange: (sources: string[]) => void

  year: number
  onYearChange: (value: number) => void
  yearOptions: number[]

  monthFilter: number | undefined
  onMonthChange: (value: number | undefined) => void
  monthOptions: MonthOption[]

  clientFilter: string | undefined
  onClientChange: (value: string | undefined) => void
  clientOptions: ClientOption[]

  reconStatuses: string[]
  onReconStatusesChange: (statuses: string[]) => void

  splitOnly: boolean
  onSplitOnlyChange: (value: boolean) => void

  hasAbsorption: boolean
  onHasAbsorptionChange: (value: boolean) => void

  noteTypes: string[]
  onNoteTypesChange: (types: string[]) => void
  noteSearch: string
  onNoteSearchChange: (value: string) => void
  onApplyNoteSearch: (value: string) => void

  onReset: () => void
  onExport: () => void
  exportDisabled?: boolean
}

const reconStatusSummary = (selected: string[]): string => {
  if (
    selected.length === 0 ||
    selected.length === RECON_STATUS_OPTIONS.length
  ) {
    return 'All statuses'
  }
  if (selected.length === 1) {
    return (
      RECON_STATUS_OPTIONS.find((o) => o.value === selected[0])?.label ??
      selected[0]
    )
  }
  return `${selected.length} statuses`
}

const noteTypeSummary = (selected: string[]): string => {
  if (selected.length === 1) {
    return (
      NOTE_TYPE_OPTIONS.find((o) => o.value === selected[0])?.label ??
      selected[0]
    )
  }
  return selected.length === NOTE_TYPE_OPTIONS.length
    ? 'Any note'
    : `${selected.length} note types`
}

/** Backend-enforced bound on `noteSearch`. */
const NOTE_SEARCH_MAX_LENGTH = 200

// Matching is by substring, term by term — abbreviations don't match.
const NOTE_SEARCH_HINT =
  'Search a word that appears in the note, e.g. "partial refund". Every word must appear in the same note.'

const TransactionFilters: React.FC<TransactionFiltersProps> = ({
  search,
  onSearchChange,
  onApplySearch,
  selectedSources,
  onSourcesChange,
  year,
  onYearChange,
  yearOptions,
  monthFilter,
  onMonthChange,
  monthOptions,
  clientFilter,
  onClientChange,
  clientOptions,
  reconStatuses,
  onReconStatusesChange,
  splitOnly,
  onSplitOnlyChange,
  hasAbsorption,
  onHasAbsorptionChange,
  noteTypes,
  onNoteTypesChange,
  noteSearch,
  onNoteSearchChange,
  onApplyNoteSearch,
  onReset,
  onExport,
  exportDisabled,
}) => (
  <div className={styles.bar}>
    <input
      className={styles.search}
      placeholder="Search"
      value={search}
      onChange={(e) => {
        const value = e.target.value
        onSearchChange(value)
        if (value === '') onApplySearch('')
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onApplySearch(search)
      }}
    />

    <PaymentTypeFilter
      selectedSources={selectedSources}
      onApply={onSourcesChange}
    />

    <Select<number>
      value={year}
      onChange={(value) => onYearChange(value)}
      style={{ width: 150 }}
      className={styles.select}
      options={yearOptions.map((y) => ({ value: y, label: String(y) }))}
    />

    <Select<number | undefined>
      allowClear
      value={monthFilter}
      onChange={(value) => onMonthChange(value ?? undefined)}
      style={{ width: 150 }}
      className={styles.select}
      placeholder="All months"
      options={monthOptions.map((m) => ({ value: m.value, label: m.label }))}
    />

    <Select<string | undefined>
      allowClear
      showSearch
      value={clientFilter}
      onChange={(value) => onClientChange(value || undefined)}
      style={{ width: 230 }}
      className={styles.select}
      placeholder="All clients"
      optionFilterProp="label"
      options={clientOptions}
    />

    <Select<string[]>
      mode="multiple"
      value={reconStatuses}
      onChange={(value) =>
        onReconStatusesChange(Array.isArray(value) ? value : [])
      }
      style={{ width: 170 }}
      className={styles.select}
      placeholder="Recon status"
      maxTagCount={0}
      maxTagPlaceholder={() => reconStatusSummary(reconStatuses)}
      options={RECON_STATUS_OPTIONS.map((o) => ({
        label: o.label,
        value: o.value,
      }))}
    />

    <Select<string[]>
      mode="multiple"
      value={noteTypes}
      onChange={(value) => onNoteTypesChange(Array.isArray(value) ? value : [])}
      style={{ width: 170 }}
      className={styles.select}
      placeholder="Note type"
      maxTagCount={0}
      maxTagPlaceholder={() => noteTypeSummary(noteTypes)}
      options={NOTE_TYPE_OPTIONS.map((o) => ({
        label: o.label,
        value: o.value,
      }))}
    />

    <input
      className={styles.search}
      placeholder='Note e.g. "partial refund"'
      maxLength={NOTE_SEARCH_MAX_LENGTH}
      title={NOTE_SEARCH_HINT}
      value={noteSearch}
      onChange={(e) => {
        const value = e.target.value
        onNoteSearchChange(value)
        if (value === '') onApplyNoteSearch('')
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onApplyNoteSearch(noteSearch)
      }}
    />

    <Checkbox
      checked={splitOnly}
      onChange={(e) => onSplitOnlyChange(e.target.checked)}
    >
      Split only
    </Checkbox>

    <Checkbox
      checked={hasAbsorption}
      onChange={(e) => onHasAbsorptionChange(e.target.checked)}
    >
      Has absorption
    </Checkbox>

    <button type="button" className={styles.resetLink} onClick={onReset}>
      Reset
    </button>

    <div className={styles.spacer} />

    <button
      type="button"
      className={styles.exportBtn}
      onClick={onExport}
      disabled={exportDisabled}
    >
      Export
    </button>
  </div>
)

export default TransactionFilters
